import { Drawer } from '../../components/controls/drawer';
import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { axiosInstance, getEnvironment, RequestConfig } from '@unifocus/js-utils';
import { ApplicationNavPanelMenu } from './ApplicationNavPanelMenu';
import { VerticalPanel } from '../../components/layout/verticalpanel';
import { VerticalSpacer } from '../../components/layout/verticalspacer';
import { HorizontalPanel } from '../../components/layout/horizontalpanel';
import { Icons } from '@unifocus/react-icons';
import { Heading } from '../../components/display/heading';
import { Button } from '../../components/controls/button';

export interface ApplicationNavPanelMenuDrawerProps {
   visible?: boolean;
   setVisible: Dispatch<SetStateAction<boolean>>;
   onClose?: () => void;
}

export const ApplicationNavPanelMenuDrawer = (props: ApplicationNavPanelMenuDrawerProps) => {
   const [menuContent, setMenuContent] = useState<string | ReactElement>('');
   const [menuUrl, setMenuUrl] = useState<string | undefined>();
   const [visible, setVisible] = useState(false);
   const [refreshedMenuContent, setRefreshedMenuContent] = useState<string>('');
   const [lastMenuContentLoadTime, setLastMenuContentLoadTime] = useState<Date | undefined>();

   const reloadMenu = React.useCallback(() => {
      if (menuUrl) {
         const axiosConfig: RequestConfig = { disableErrorHandling: true };
         axiosInstance.get(menuUrl, axiosConfig).then(response => {
            if (!menuContent) {
               setMenuContent(response.data);
            }
            else {
               setRefreshedMenuContent(response.data);
            }
            setLastMenuContentLoadTime(new Date());
         }).catch(() => {
            console.error('Error loading mobile menu');

            if (!menuContent) {
               setMenuContent(menuErrorPanel(reloadMenuIfNecessary, setMenuContent));
            }
         });
      }
   }, [menuContent, refreshedMenuContent, lastMenuContentLoadTime, menuUrl]);

   const reloadMenuIfNecessary = React.useCallback(() => {
      if (menuUrl) {
         const RELOAD_MILLIS = 3 * 60 * 1000; //3 mins
         const backgroundRefreshedDataIsReady = !!refreshedMenuContent;

         if (backgroundRefreshedDataIsReady) {
            setMenuContent(refreshedMenuContent);
            setRefreshedMenuContent('');
         }

         const needToRefreshData = !lastMenuContentLoadTime || new Date().getTime() - lastMenuContentLoadTime.getTime() > RELOAD_MILLIS;

         if (needToRefreshData) {
            reloadMenu();
         }
      }
   }, [refreshedMenuContent, lastMenuContentLoadTime, menuUrl, reloadMenu]);

   const onClose = React.useCallback(() => {
      props.setVisible(false);
   }, [props.setVisible]);

   useEffect(() => {
      const url = getEnvironment().portalMenuUrl;
      setMenuUrl(url);
   }, []);

   useEffect(() => {
      reloadMenuIfNecessary();
   }, [reloadMenuIfNecessary, menuUrl]);

   useEffect(() => {
      if (props.visible) {
         reloadMenuIfNecessary();
      }

      setVisible(!!props.visible);
   }, [props.visible]);

   return (
      <Drawer
         className={'uf-application-nav-panel-drawer'}
         visible={visible}
         onClose={() => {
            props.setVisible(false);
         }}
      >
         {menuComponent(menuContent, onClose)}
      </Drawer>
   );
};

const menuComponent = (menuContent: string | ReactElement, onClose: () => void) => {
   if (typeof menuContent === 'string') {
      return <ApplicationNavPanelMenu menuContent={menuContent}
                                      hideMenu={onClose}/>;
   }

   return menuContent;
};

const menuErrorPanel = (reloadMenuIfNecessary: any, setMenuVisible: any) => {
   return (
      <VerticalPanel gap={true} padding={true}>
         <VerticalSpacer size={'medium'}/>
         <HorizontalPanel align={'center'} verticalFill={false}>
            <Icons.Error size={3} style={{ color: 'white' }}/>
         </HorizontalPanel>
         <HorizontalPanel align={'center'} verticalFill={false}>
            <Heading level={3} style={{ color: 'white' }}>
               Unable to load the menu.
            </Heading>
         </HorizontalPanel>
         <Button
            onClick={() => {
               reloadMenuIfNecessary();
               setMenuVisible(true);
            }}
         >
            Retry
         </Button>
      </VerticalPanel>
   );
};
